export const SESSION_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  AI_ASSISTANT_CHAT_ENTRY: 'ai-assistant-chat-entry',
  APPOINTMENT_ID: 'appointmentId',
  APPOINTMENT_IS_SCHEDULED: 'appointmentIsScheduled',
  COMPLETED_SIGNUP: 'completedSignup',
  DIABETES_TYPE_ONE_ANSWER: 'diabetes_type_one_answer',
  FLOW_EXITED: 'flow-exited',
  IDENTITY_VERIFICATION_SKIPPED: 'isIdentityVerificationSkipped',
  IDENTITY_VERIFIED: 'identity-verified',
  INSURANCE_AVAILABLE_IN_STATE: 'isInsuranceAvailableInUsersState',
  IS_RESULTS_LOADED: 'isResultsLoaded',
  MARKETING_PARAMS: 'marketing-params',
  MOBILE_WEBVIEW: 'mobile-webview',
  ONBOARDING_ID: 'onboarding-id',
  ORCHESTRATE_ACCESS_TOKEN: 'orchestrateAccessToken',
  ORCHESTRATE_PARTNER_TOKEN: 'orchestratePartnerToken',
  ORCHESTRATE_RESTORE_TOKEN: 'orchestrateRestoreToken',
  PO_PHARMACY_STEP_SKIPPED: 'isPoPharmacyStepSkipped',
  REFRESH_TOKEN: 'refreshToken',
  REQUIRE_SYNC_APPOINTMENT: 'requireSyncAppointment',
  RESTORED_SESSION: 'restoredSession',
  RETRY_LAZY_REFRESHED: 'retry-lazy-refreshed',
  SESSION_ID: 'session-id',
  SIGNUP_ACCESS_TOKEN: 'signupAccessToken',
  SIGNUP_REFRESH_TOKEN: 'signupRefreshToken',
  STAFF_ID: 'staffId',
  SUGGEST_WM_AFTER_LOGIN: 'suggest-wm-after-login',
  VIEW_AS_PATIENT: 'viewAsPatient',
  WANT_TO_USE_INSURANCE: 'wantToUseInsurance',
  WOMEN_HEALTH: 'womenHealth'
} as const;

export const LOCAL_KEYS = {
  LOGOUT_EVENT: 'logout-event',
  PERSISTENT_SCROLL: 'persistentScroll',
  THEME: 'theme'
} as const;

type SessionKey = (typeof SESSION_KEYS)[keyof typeof SESSION_KEYS];
type LocalKey = (typeof LOCAL_KEYS)[keyof typeof LOCAL_KEYS];

export type SessionAuthTokens = {
  accessToken?: string;
  refreshToken?: string;
};

export type SessionOrchestrateAuthTokens = {
  accessToken?: string;
  partnerToken?: string;
  restoreToken?: string;
};

/**
 * Returns `window.localStorage` when readable, otherwise `null`.
 * Property access can throw SecurityError (blocked storage, third-party context) or yield `null` (some embedded WebViews).
 */
export function getBrowserLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage ?? null;
  } catch {
    return null;
  }
}

function getBrowserSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export const session = {
  clear: () => {
    try {
      getBrowserSessionStorage()?.clear();
    } catch {
      // storage unavailable
    }
  },
  get: (key: SessionKey) => {
    try {
      return getBrowserSessionStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  remove: (key: SessionKey) => {
    try {
      getBrowserSessionStorage()?.removeItem(key);
    } catch {
      // storage unavailable
    }
  },
  set: (key: SessionKey, value: string) => {
    try {
      getBrowserSessionStorage()?.setItem(key, value);
    } catch {
      // storage unavailable
    }
  }
};

const setOrRemoveSessionValue = (key: SessionKey, value?: null | string) => {
  if (value) {
    session.set(key, value);
  } else {
    session.remove(key);
  }
};

export const getSessionAuthTokens = (): Required<SessionAuthTokens> => ({
  accessToken: session.get(SESSION_KEYS.ACCESS_TOKEN) ?? '',
  refreshToken: session.get(SESSION_KEYS.REFRESH_TOKEN) ?? ''
});

export const setSessionAuthTokens = ({ accessToken, refreshToken }: SessionAuthTokens) => {
  setOrRemoveSessionValue(SESSION_KEYS.ACCESS_TOKEN, accessToken);
  setOrRemoveSessionValue(SESSION_KEYS.REFRESH_TOKEN, refreshToken);
};

export const clearSessionAuthTokens = () => {
  session.remove(SESSION_KEYS.ACCESS_TOKEN);
  session.remove(SESSION_KEYS.REFRESH_TOKEN);
};

export const getSessionSignUpAuthTokens = (): Required<SessionAuthTokens> => ({
  accessToken: session.get(SESSION_KEYS.SIGNUP_ACCESS_TOKEN) ?? '',
  refreshToken: session.get(SESSION_KEYS.SIGNUP_REFRESH_TOKEN) ?? ''
});

export const setSessionSignUpAuthTokens = ({ accessToken, refreshToken }: SessionAuthTokens) => {
  setOrRemoveSessionValue(SESSION_KEYS.SIGNUP_ACCESS_TOKEN, accessToken);
  setOrRemoveSessionValue(SESSION_KEYS.SIGNUP_REFRESH_TOKEN, refreshToken);
};

export const clearSessionSignUpAuthTokens = () => {
  session.remove(SESSION_KEYS.SIGNUP_ACCESS_TOKEN);
  session.remove(SESSION_KEYS.SIGNUP_REFRESH_TOKEN);
};

export const getSessionOrchestrateAuthTokens = (): SessionOrchestrateAuthTokens => ({
  accessToken: session.get(SESSION_KEYS.ORCHESTRATE_ACCESS_TOKEN) ?? undefined,
  partnerToken: session.get(SESSION_KEYS.ORCHESTRATE_PARTNER_TOKEN) ?? undefined,
  restoreToken: session.get(SESSION_KEYS.ORCHESTRATE_RESTORE_TOKEN) ?? undefined
});

export const setSessionOrchestrateAuthTokens = ({
  accessToken,
  partnerToken,
  restoreToken
}: SessionOrchestrateAuthTokens) => {
  setOrRemoveSessionValue(SESSION_KEYS.ORCHESTRATE_ACCESS_TOKEN, accessToken);
  setOrRemoveSessionValue(SESSION_KEYS.ORCHESTRATE_PARTNER_TOKEN, partnerToken);
  setOrRemoveSessionValue(SESSION_KEYS.ORCHESTRATE_RESTORE_TOKEN, restoreToken);
};

export const clearSessionOrchestrateAuthTokens = () => {
  session.remove(SESSION_KEYS.ORCHESTRATE_ACCESS_TOKEN);
  session.remove(SESSION_KEYS.ORCHESTRATE_PARTNER_TOKEN);
  session.remove(SESSION_KEYS.ORCHESTRATE_RESTORE_TOKEN);
};

export const clearAllSessionAuthTokens = () => {
  clearSessionAuthTokens();
  clearSessionSignUpAuthTokens();
  clearSessionOrchestrateAuthTokens();
};

/**
 * Drops what makes this tab a staff member acting as a patient: the view-as-patient marker the AI
 * chat, Zoom channel, click-to-call and Main layout read, and the staff id sent as
 * `impersonation-mode-by` on every request. Both belong to the session that set them. They go when
 * that session is cleared (`user/clearUser`, through clearStaffViewAsPatientMiddleware) and when a
 * direct sign-in takes its place (Login, the WorkOS session load, ConfirmAccount); a token renewal
 * is neither.
 */
export const clearStaffViewAsPatient = () => {
  session.remove(SESSION_KEYS.VIEW_AS_PATIENT);
  session.remove(SESSION_KEYS.STAFF_ID);
};

export const local = {
  clear: () => {
    try {
      getBrowserLocalStorage()?.clear();
    } catch {
      // storage unavailable
    }
  },
  get: (key: LocalKey) => {
    try {
      return getBrowserLocalStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  remove: (key: LocalKey) => {
    try {
      getBrowserLocalStorage()?.removeItem(key);
    } catch {
      // storage unavailable
    }
  },
  set: (key: LocalKey, value: string) => {
    try {
      getBrowserLocalStorage()?.setItem(key, value);
    } catch {
      // storage unavailable
    }
  }
};
